import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Package,
  Calendar,
  Building,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  packingApiService, 
  PackingHeader, 
  PackingStatus
} from '../services/packingApi';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const PackingEntryViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id, batchId } = useParams<{ id?: string; batchId?: string }>();
  
  // State management
  const [packingHeader, setPackingHeader] = useState<PackingHeader | null>(null);
  const [batchHeaders, setBatchHeaders] = useState<PackingHeader[]>([]);
  const [packingDetails, setPackingDetails] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const workOrdersInBatch = React.useMemo(() => {
    const rows = (batchId ? batchHeaders : packingHeader ? [packingHeader] : [])
      .map((h) => ({
        workOrderId: h.workOrderId,
        workOrderNumber: h.workOrderNumber,
      }))
      .filter((w) => Boolean(w.workOrderId));

    const byId = new Map<number, { workOrderId: number; workOrderNumber?: string }>();
    rows.forEach((w) => {
      const id = Number(w.workOrderId);
      if (!Number.isFinite(id)) return;
      if (!byId.has(id)) byId.set(id, { workOrderId: id, workOrderNumber: w.workOrderNumber });
    });
    return Array.from(byId.values()).sort((a, b) => (a.workOrderNumber || '').localeCompare(b.workOrderNumber || ''));
  }, [batchId, batchHeaders, packingHeader]);

  const boxTypesInBatch = React.useMemo(() => {
    const rows = (batchId ? batchHeaders : packingHeader ? [packingHeader] : [])
      .map((h) => ({
        boxTypeId: h.boxTypeId,
        boxTypeName: h.boxTypeName,
      }));

    const byName = new Map<string, { boxTypeName: string; boxTypeId?: number }>();
    rows.forEach((r) => {
      const name = r.boxTypeName?.trim();
      if (!name) return;
      if (!byName.has(name)) {
        byName.set(name, { boxTypeName: name, boxTypeId: r.boxTypeId });
      }
    });

    // If API doesn't send boxTypeName, fall back to ID-based label
    if (byName.size === 0) {
      const byId = new Map<number, { boxTypeName: string; boxTypeId: number }>();
      rows.forEach((r) => {
        const id = Number(r.boxTypeId);
        if (!Number.isFinite(id)) return;
        if (!byId.has(id)) byId.set(id, { boxTypeId: id, boxTypeName: `Box Type #${id}` });
      });
      return Array.from(byId.values()).sort((a, b) => a.boxTypeId - b.boxTypeId);
    }

    return Array.from(byName.values()).sort((a, b) => a.boxTypeName.localeCompare(b.boxTypeName));
  }, [batchId, batchHeaders, packingHeader]);

  // Load packing header/batch data
  useEffect(() => {
    if (batchId) {
      loadPackingBatch(batchId);
      return;
    }
    if (id) {
      loadPackingHeader(id);
    }
  }, [id, batchId]);

  const loadPackingHeader = async (headerId: string) => {
    try {
      const response = await packingApiService.getPackingHeaderById(headerId);
      if (response.success) {
        const header = response.data;
        // Prefer batch-based view URL when batchId exists
        if (!batchId && header?.batchId) {
          navigate(`/packing-entries/batch/${header.batchId}`, { replace: true });
          return;
        }
        setPackingHeader(header);
        setBatchHeaders([]);
        
        // Load packing details separately
        try {
          const detailsResponse = await packingApiService.getPackingDetailsByHeader(headerId);
          if (detailsResponse.success) {
            setPackingDetails(detailsResponse.data || []);
          }
        } catch (error) {
          // console.error('Error loading packing details:', error);
          setPackingDetails([]);
        }
      }
    } catch (error) {
      // console.error('Error loading packing header:', error);
      toast.error('Failed to load packing header details');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPackingBatch = async (batchIdParam: string) => {
    try {
      // batchId can be a numeric id OR a batch code (e.g. "BH-SGI-20260413-0003")
      const response = await packingApiService.getPackingHeadersByBatchId(batchIdParam);
      if (!response.success || !Array.isArray(response.data) || response.data.length === 0) {
        setPackingHeader(null);
        setBatchHeaders([]);
        setPackingDetails([]);
        return;
      }

      const headers = response.data;
      setBatchHeaders(headers);

      // Create a display header by using the first header + aggregated totals
      const first = headers[0];
      const totalQty = headers.reduce((sum, h) => sum + (Number(h.quantity) || 0), 0);
      const totalRejectQty = headers.reduce((sum, h) => sum + (Number(h.rejectQuantity) || 0), 0);
      const totalBoxes = headers.reduce((sum, h) => sum + (Number(h.totalBoxes) || 0), 0);

      setPackingHeader({
        ...first,
        quantity: totalQty,
        rejectQuantity: totalRejectQty,
        totalBoxes,
      });

      // Load boxes/details for the batch in a single call
      try {
        const detailsResponse = await packingApiService.getPackingDetailsByBatchId(batchIdParam);
        if (detailsResponse.success && Array.isArray(detailsResponse.data)) {
          setPackingDetails(detailsResponse.data || []);
        } else {
          setPackingDetails([]);
        }
      } catch {
        setPackingDetails([]);
      }

    } catch (error) {
      toast.error('Failed to load packing batch details');
      setPackingHeader(null);
      setBatchHeaders([]);
      setPackingDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get status icon
  const getStatusIcon = (status: PackingStatus) => {
    switch (status) {
      case PackingStatus.PACKED:
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case PackingStatus.INVOICED:
        return <FileText className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: PackingStatus) => {
    switch (status) {
      case PackingStatus.PACKED:
        return 'default';
      case PackingStatus.INVOICED:
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">Loading packing header details...</span>
        </div>
      </div>
    );
  }

  if (!packingHeader) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {batchId ? 'Packing Batch Not Found' : 'Packing Header Not Found'}
            </h2>
            <p className="text-gray-600 mb-6">
              {batchId
                ? "The packing batch you're looking for doesn't exist or has been removed."
                : "The packing header you're looking for doesn't exist or has been removed."}
            </p>
            <Button 
              onClick={() => navigate('/packing-entries')} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Packing Headers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/packing-entries')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Packing Headers
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {batchId ? 'Packing Batch Details' : 'Packing Header Details'}
            </h1>
            <p className="text-gray-600 mt-1">View packing header information and related details</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Basic Information */}
        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <Package className="h-5 w-5 mr-3 text-primary-600" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Packing Header Number</Label>
                <p className="text-xl font-bold text-gray-900">{packingHeader.packingHeaderNo}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Status</Label>
                <div className="flex items-center space-x-2">
                  {packingHeader.status ? getStatusIcon(packingHeader.status) : null}
                  <Badge variant={getStatusBadgeVariant(packingHeader.status || PackingStatus.PACKED)} className="px-3 py-1 text-sm">
                    {packingHeader.status || 'N/A'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Quantity</Label>
                <p className="text-xl font-bold text-green-600">{packingHeader.quantity}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Total Boxes</Label>
                <p className="text-xl font-bold text-blue-600">{packingDetails.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dates and User Information */}
        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <Calendar className="h-5 w-5 mr-3 text-primary-600" />
              Dates and User Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Packed On</Label>
                <p className="text-xl font-bold text-gray-900">
                  {packingHeader.packedOn ? formatDate(packingHeader.packedOn) : 'Not packed yet'}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600">Packed By</Label>
                <p className="text-xl font-bold text-gray-900">{packingHeader.packedBy || 'Not assigned'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Work Order Information */}
        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <FileText className="h-5 w-5 mr-3 text-primary-600" />
              Work Order Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {workOrdersInBatch.length > 0 ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-600">Work Orders</Label>
                <div className="flex flex-wrap gap-2">
                  {workOrdersInBatch.map((wo) => (
                    <a
                      key={wo.workOrderId}
                      href={`/work-orders/${wo.workOrderId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-800 hover:bg-blue-100 hover:underline"
                      title={`Open Work Order ${wo.workOrderId}`}
                    >
                      {wo.workOrderNumber || `WO #${wo.workOrderId}`}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-2 text-gray-500">Work order information not available</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* End Good Information */}
      {/* End Good Information removed (use packingHeader fields only) */}

      {/* Sales Order Information */}
      {packingHeader.salesOrder && (
        <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
            <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
              <FileText className="h-5 w-5 mr-3 text-primary-600" />
              Sales Order Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Sales Order Number</Label>
                <p className="text-lg font-semibold text-gray-900">{packingHeader.salesOrder.soNumber}</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-gray-600">Customer</Label>
                <p className="text-lg font-semibold text-gray-900">
                  {packingHeader.salesOrder.customerName || 'N/A'}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Order Date</Label>
                <p className="text-lg font-semibold text-gray-900">
                  {packingHeader.salesOrder.orderDate ? formatDate(packingHeader.salesOrder.orderDate) : 'N/A'}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Sales Order Status</Label>
                <Badge variant="outline" className="mt-1">{packingHeader.salesOrder.status}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Packing Details */}
      <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
          <CardTitle className="flex items-center justify-between text-primary-800 text-lg font-semibold">
            <div className="flex items-center">
              <Package className="h-5 w-5 mr-3 text-primary-600" />
              Packing Details
            </div>
            {packingDetails && packingDetails.length > 0 && (
              <span className="text-sm font-medium bg-primary-100 px-3 py-1 rounded-full text-primary-700">
                Total Boxes: {packingDetails.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {packingDetails && packingDetails.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-primary-50 border-b-2 border-primary-200">
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Box Number</th>
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Quantity</th>
                    <th className="text-left py-3 px-4 font-bold text-primary-800 text-sm uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {packingDetails.map((detail: any, index: number) => (
                    <tr 
                      key={detail.id} 
                      className={`border-b border-primary-100 hover:bg-primary-50 transition-colors duration-200 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-primary-25'
                      }`}
                    >
                      <td className="py-3 px-4 font-semibold text-gray-900 text-base">{detail.boxNumber}</td>
                      <td className="py-3 px-4 text-gray-700 font-medium text-base">{detail.quantity}</td>
                      <td className="py-3 px-4">
                        <Badge 
                          variant={detail.status === 'FULL' ? 'default' : 'outline'}
                          className={`${
                            detail.status === 'FULL' 
                              ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' 
                              : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-300'
                          } font-semibold px-2 py-1 text-xs`}
                        >
                          {detail.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500 text-lg">No packing details available</p>
              <p className="text-gray-400 text-sm">Packing details will appear here once boxes are created</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Box Type Information */}
      <Card className="border border-gray-200 rounded-xl shadow-sm bg-white">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-100 rounded-t-xl">
          <CardTitle className="flex items-center text-primary-800 text-lg font-semibold">
            <Package className="h-5 w-5 mr-3 text-primary-600" />
            Box Type Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          {boxTypesInBatch.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-600">Box Types</Label>
              <div className="flex flex-wrap gap-2">
                {boxTypesInBatch.map((bt) => (
                  <span
                    key={`${bt.boxTypeName}-${bt.boxTypeId ?? ''}`}
                    className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-1 text-sm font-semibold text-gray-900"
                    title={bt.boxTypeId ? `Box Type ID: ${bt.boxTypeId}` : undefined}
                  >
                    {bt.boxTypeName}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-2 text-gray-500">Box type information not available</div>
          )}
        </CardContent>
      </Card>

      {/* Branch Information removed per requirement */}
    </div>
  );
};

export default PackingEntryViewPage;
